import { FC, useEffect, useMemo, useState } from 'react';

import { router } from '@inertiajs/react';
import { Loader2 } from 'lucide-react';

import { Button } from '../../../../components/ui/Button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../../../components/ui/Dialog';
import { Label } from '../../../../components/ui/Label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../../components/ui/Select';
import { Textarea } from '../../../../components/ui/Textarea';
import { Routes } from '../../../../models/routes';
import { OfferDraft, Universe } from '../../../../types';
import {
    AutoPriceAdjustmentMode,
    MAX_OFFER_DRAFTS_COUNT,
    serializeOfferDraft,
} from '../__shared/offerDraft';

const SUPPORTED_IMPORT_UNIVERSE_SLUGS = [
    'one-piece-tcg',
    'magic-the-gathering',
] as const;

const IMPORT_PLACEHOLDERS: Record<
    (typeof SUPPORTED_IMPORT_UNIVERSE_SLUGS)[number],
    string
> = {
    'one-piece-tcg': `4.00 Nami (EB02-017)
Monkey.D.Luffy (EB02-010)`,
    'magic-the-gathering': `1 Gandalf the White [LTR]
Oko, Thief of Crowns [ELD]`,
};

const ONE_PIECE_SECTION_LINE =
    /^(leader|character|event|stage|don)(?:\s+\(\d+\))?$/i;

const countImportRows = (
    text: string,
    universeSlug?: (typeof SUPPORTED_IMPORT_UNIVERSE_SLUGS)[number],
) =>
    text.split(/\r?\n/).filter((line) => {
        const normalizedLine = line.trim();
        if (!normalizedLine) return false;

        return !(
            universeSlug === 'one-piece-tcg' &&
            ONE_PIECE_SECTION_LINE.test(normalizedLine)
        );
    }).length;

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    universes: Universe[];
    defaultUniverseId?: number | null;
    currentImportBatchId?: number | null;
    existingDrafts: OfferDraft[];
    autoPrice: boolean;
    autoPriceCurrency: 'EUR' | 'USD';
    autoPriceMarkup: number;
    autoPriceAdjustmentMode: AutoPriceAdjustmentMode;
    autoPriceApplyToManual: boolean;
}

export const OfferImportModal: FC<Props> = ({
    open,
    onOpenChange,
    universes,
    defaultUniverseId,
    currentImportBatchId,
    existingDrafts,
    autoPrice,
    autoPriceCurrency,
    autoPriceMarkup,
    autoPriceAdjustmentMode,
    autoPriceApplyToManual,
}) => {
    const supportedUniverses = useMemo(
        () =>
            universes.filter((universe) =>
                SUPPORTED_IMPORT_UNIVERSE_SLUGS.includes(
                    universe.slug as (typeof SUPPORTED_IMPORT_UNIVERSE_SLUGS)[number],
                ),
            ),
        [universes],
    );
    const fallbackUniverseId = supportedUniverses[0]?.id ?? null;
    const [selectedUniverseId, setSelectedUniverseId] = useState<number | null>(
        defaultUniverseId ?? fallbackUniverseId,
    );
    const [codesText, setCodesText] = useState('');
    const [processing, setProcessing] = useState(false);
    const [errors, setErrors] = useState<{
        import_universe_id?: string;
        import_codes_text?: string;
    }>({});
    const selectedUniverseSlug = useMemo(
        () =>
            supportedUniverses.find(
                (universe) => universe.id === selectedUniverseId,
            )?.slug as
                | (typeof SUPPORTED_IMPORT_UNIVERSE_SLUGS)[number]
                | undefined,
        [selectedUniverseId, supportedUniverses],
    );
    const importPlaceholder =
        (selectedUniverseSlug
            ? IMPORT_PLACEHOLDERS[selectedUniverseSlug]
            : null) ??
        `4.00 Nami (EB02-017)
1 Gandalf the White [LTR]`;
    const pastedRowsCount = useMemo(
        () => countImportRows(codesText, selectedUniverseSlug),
        [codesText, selectedUniverseSlug],
    );
    const totalRowsCount = existingDrafts.length + pastedRowsCount;
    const exceedsRowsLimit = totalRowsCount > MAX_OFFER_DRAFTS_COUNT;

    useEffect(() => {
        if (!open) return;

        const nextUniverseId =
            supportedUniverses.find(
                (universe) => universe.id === defaultUniverseId,
            )?.id ?? fallbackUniverseId;
        setSelectedUniverseId(nextUniverseId);
    }, [defaultUniverseId, fallbackUniverseId, open, supportedUniverses]);

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen && processing) {
            return;
        }

        onOpenChange(nextOpen);

        if (nextOpen) return;

        setErrors({});
        setProcessing(false);
    };

    const handleSubmit = () => {
        if (exceedsRowsLimit) {
            setErrors({
                import_codes_text: `У черзі може бути не більше ${MAX_OFFER_DRAFTS_COUNT} рядків`,
            });
            return;
        }

        setProcessing(true);
        setErrors({});

        router.post(
            Routes.ImportPreviewOffers,
            {
                import_batch_id: currentImportBatchId,
                import_universe_id: selectedUniverseId,
                import_codes_text: codesText,
                auto_price: autoPrice,
                auto_price_currency: autoPriceCurrency,
                auto_price_markup: autoPriceMarkup,
                auto_price_adjustment_mode: autoPriceAdjustmentMode,
                auto_price_apply_to_manual: autoPriceApplyToManual,
                existing_drafts: existingDrafts.map(serializeOfferDraft),
            },
            {
                preserveScroll: true,
                preserveState: 'errors',
                onError: (pageErrors) => {
                    setErrors({
                        import_universe_id: pageErrors.import_universe_id as
                            | string
                            | undefined,
                        import_codes_text: pageErrors.import_codes_text as
                            | string
                            | undefined,
                    });
                },
                onFinish: () => {
                    setProcessing(false);
                },
            },
        );
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-h-[90vh] w-[95vw] max-w-2xl overflow-y-auto border-input sm:w-full">
                <DialogHeader className="space-y-1">
                    <DialogTitle>Імпорт списком</DialogTitle>
                    <DialogDescription>
                        Вставте список товарів, а ми підготуємо чергу для
                        подальшого редагування цін і параметрів.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid gap-3 pt-1 pb-2">
                    <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-xs text-blue-900 dark:border-blue-900/50 dark:bg-blue-950/30 dark:text-blue-200">
                        <ul className="list-disc space-y-1 pl-4">
                            <li>
                                Один рядок = один товар. Якщо кількість не
                                вказана, автоматично ставимо 1.
                            </li>
                            <li>
                                Для MTG бажано вказувати сет у форматі [M20], а
                                фойл позначати як (F).
                            </li>
                            <li>
                                У черзі може бути до {MAX_OFFER_DRAFTS_COUNT}{' '}
                                рядків.
                            </li>
                        </ul>
                    </div>

                    <div className="grid gap-2">
                        <Label htmlFor="offer-import-universe">Гра</Label>
                        <Select
                            value={selectedUniverseId?.toString()}
                            disabled={processing}
                            onValueChange={(value) => {
                                setSelectedUniverseId(Number(value));
                                setErrors((currentErrors) => ({
                                    ...currentErrors,
                                    import_universe_id: undefined,
                                }));
                            }}
                        >
                            <SelectTrigger
                                id="offer-import-universe"
                                className="h-8 text-sm sm:h-9"
                            >
                                <SelectValue placeholder="Оберіть гру" />
                            </SelectTrigger>
                            <SelectContent>
                                {supportedUniverses.map((universe) => (
                                    <SelectItem
                                        key={universe.id}
                                        value={universe.id.toString()}
                                    >
                                        {universe.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {errors.import_universe_id && (
                            <p className="text-sm text-red-600">
                                {errors.import_universe_id}
                            </p>
                        )}
                    </div>

                    <div className="grid gap-2">
                        <Label htmlFor="offer-import-codes">
                            Список карток
                        </Label>
                        <Textarea
                            id="offer-import-codes"
                            value={codesText}
                            onChange={(event) => {
                                setCodesText(event.target.value);
                                setErrors((currentErrors) => ({
                                    ...currentErrors,
                                    import_codes_text: undefined,
                                }));
                            }}
                            className="min-h-[260px]"
                            placeholder={importPlaceholder}
                            disabled={processing}
                        />
                        <p
                            className={`text-xs ${exceedsRowsLimit ? 'font-medium text-red-600' : 'text-muted-foreground'}`}
                            aria-live="polite"
                        >
                            У черзі після імпорту: {totalRowsCount} /{' '}
                            {MAX_OFFER_DRAFTS_COUNT}
                        </p>
                        {exceedsRowsLimit && !errors.import_codes_text && (
                            <p className="text-sm text-red-600">
                                У черзі може бути не більше{' '}
                                {MAX_OFFER_DRAFTS_COUNT} рядків. Приберіть{' '}
                                {totalRowsCount - MAX_OFFER_DRAFTS_COUNT}.
                            </p>
                        )}
                        {errors.import_codes_text && (
                            <p className="text-sm text-red-600">
                                {errors.import_codes_text}
                            </p>
                        )}
                    </div>
                </div>

                <DialogFooter className="flex-col gap-2 sm:flex-row sm:gap-0">
                    <Button
                        type="button"
                        variant="outline"
                        className="mt-2 h-9 w-full text-sm sm:mt-0 sm:w-auto"
                        onClick={() => handleOpenChange(false)}
                        disabled={processing}
                    >
                        Скасувати
                    </Button>
                    <Button
                        type="button"
                        className="h-9 w-full text-sm sm:w-auto"
                        onClick={handleSubmit}
                        disabled={
                            processing ||
                            supportedUniverses.length === 0 ||
                            exceedsRowsLimit ||
                            pastedRowsCount === 0
                        }
                    >
                        {processing ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Імпортуємо...
                            </>
                        ) : (
                            'Імпортувати в чергу'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
